import React, { useMemo, useEffect, useRef, useCallback } from 'react';
import { LineChart, BarChart, lineElementClasses } from '@mui/x-charts';
import KPICard from '../Dashboard/KPICard';
import SmallPieChart from '../Dashboard/SmallPieChart';
import Paper from '@mui/material/Paper';
import TodaysStats from '../../types/api/TodaysStats';

interface Props { dashboardInfos?: TodaysStats }

// Component to apply dashline to second series
const ChartDashlineEffect: React.FC<{ containerRef: React.RefObject<HTMLDivElement> }> = ({ containerRef }) => {
  useEffect(() => {
    const applyDashline = () => {
      if (!containerRef.current) return;
      
      const svg = containerRef.current.querySelector('svg');
      if (!svg) return;
      
      // Get all path elements
      const allPaths = Array.from(svg.querySelectorAll('path'));
      
      // Look for paths that have a d attribute with reasonable length (actual lines)
      const linesPaths = allPaths.filter(p => {
        const d = p.getAttribute('d');
        // Lines typically have longer d attributes and start with M (move)
        return d && d.length > 100 && d.startsWith('M');
      });
      
      console.log('Line paths found:', linesPaths.length);
      
      // Apply dasharray to all but the first
      linesPaths.forEach((line, idx) => {
        if (idx > 0) {
          // Use both setAttribute and style to ensure it works
          line.setAttribute('stroke-dasharray', '5 5');
          line.style.strokeDasharray = '5 5';
          console.log('Applied dasharray to line path', idx, 'style:', line.style.strokeDasharray);
        }
      });
    };
    
    // Try multiple times as chart renders asynchronously
    const timers = [
      setTimeout(applyDashline, 100),
      setTimeout(applyDashline, 200),
      setTimeout(applyDashline, 400),
    ];
    
    return () => timers.forEach(t => clearTimeout(t));
  }, [containerRef]);
  return null;
};

const TabPerformances: React.FC<Props> = ({ dashboardInfos }) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const normalizeMonth = (m: any) => {
    if (m === undefined || m === null) return m;
    const num = Number(m);
    if (Number.isNaN(num)) return m;
    return num > 11 ? num - 1 : num; // support 1-based months from backend
  };

  const buildDateFromParts = useCallback((year?: number, month?: number, day?: number) => {
    if (typeof year !== 'number' || typeof month !== 'number' || typeof day !== 'number') return null;
    const m = normalizeMonth(month);
    return new Date(year, m, day);
  }, []);

  // derive series data
  const monthClients = useMemo(() => {
    // Prefer the Monthly.clients value if present
    if (dashboardInfos?.Monthly && dashboardInfos.Monthly?.clients !== undefined) return dashboardInfos.Monthly.clients;
    // Fallback: sum the DailyPerformances for the current month
    const arr = dashboardInfos?.DailyPerformances ?? [];
    const now = new Date();
    const month = now.getMonth();
    const year = now.getFullYear();
    return arr
      .filter(p => p.year === year && normalizeMonth(p.month) === month)
      .reduce((acc, p) => acc + (p.noonPerformance ?? 0) + (p.eveningPerformance ?? 0), 0);
  }, [dashboardInfos]);
  const monthClientsRounded = Math.round(monthClients);

  // yesterday performance - prefer explicit value, otherwise find in DailyPerformances
  const j1Noon = (() => {
    if (dashboardInfos?.yesterday?.noonPerformance !== undefined && dashboardInfos.yesterday) return dashboardInfos.yesterday.noonPerformance;
    const arr = dashboardInfos?.DailyPerformances ?? [];
    const yesterday = new Date(); yesterday.setDate(yesterday.getDate() - 1);
    const found = arr.find(p => p.year === yesterday.getFullYear() && normalizeMonth(p.month) === yesterday.getMonth() && p.dayNumber === yesterday.getDate());
    return found?.noonPerformance ?? 0;
  })();
  const j1Evening = (() => {
    if (dashboardInfos?.yesterday?.eveningPerformance !== undefined && dashboardInfos.yesterday) return dashboardInfos.yesterday.eveningPerformance;
    const arr = dashboardInfos?.DailyPerformances ?? [];
    const yesterday = new Date(); yesterday.setDate(yesterday.getDate() - 1);
    const found = arr.find(p => p.year === yesterday.getFullYear() && normalizeMonth(p.month) === yesterday.getMonth() && p.dayNumber === yesterday.getDate());
    return found?.eveningPerformance ?? 0;
  })();

  // compute monthly revenue (ca)
  const monthlyRevenue = useMemo(() => {
    if (dashboardInfos?.Monthly?.revenue !== undefined && dashboardInfos?.Monthly?.revenue !== null) return dashboardInfos.Monthly.revenue;
    // fallback: sum turnover from DailyPerformances for current month
    const arr = dashboardInfos?.DailyPerformances ?? [];
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const monthSum = arr
      .filter(p => p.year === year && normalizeMonth(p.month) === month)
      .reduce((acc, p) => acc + (((p as any).caMidi ?? (p as any).ca ?? ((p as any).noonTurnover ?? 0)) + ((p as any).caSoir ?? ((p as any).eveningTurnover ?? 0))), 0);
    if (monthSum > 0) return monthSum;
    // fallback to last 30 days turnovers
    const thirtyDaysAgo: Date = new Date(now);
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    thirtyDaysAgo.setHours(0, 0, 0, 0);
    const last30 = arr.filter(p => {
      const d = buildDateFromParts(p.year, p.month, p.dayNumber);
      return d !== null && d >= thirtyDaysAgo && d <= now;
    });
    const fallbackSum = last30.reduce((acc, p) => acc + (((p as any).caMidi ?? (p as any).ca ?? ((p as any).noonTurnover ?? 0)) + ((p as any).caSoir ?? ((p as any).eveningTurnover ?? 0))), 0);
    if (fallbackSum > 0) return fallbackSum;
    // if we still have no turnover data, attempt an estimate based on covers * avg ticket
    const avgTicket = 15; // EUR - adjust as needed or pull from settings
    const sumCovers = last30.reduce((acc, p) => acc + ((p.noonPerformance ?? 0) + (p.eveningPerformance ?? 0)), 0);
    if (sumCovers > 0) return Math.round(sumCovers * avgTicket);
    return 0;
  }, [dashboardInfos]);

  const lineData = useMemo(() => {
    const arr = dashboardInfos?.Monthly?.daily || dashboardInfos?.DailyPerformances || [];
    const now = new Date();

    const weekdayFmt = new Intl.DateTimeFormat('fr-FR', { weekday: 'long' });
    const toPoint = (p: any) => {
      const revenueRaw = p.ca ?? p.revenue ?? ((p.caMidi !== undefined || p.caSoir !== undefined) ? (Number(p.caMidi ?? 0) + Number(p.caSoir ?? 0)) : (Number(p.noonTurnover ?? 0) + Number(p.eveningTurnover ?? 0)));
      const revenue = Number(revenueRaw ?? 0);
      if (p.dateStr) {
        const d = new Date(p.dateStr);
        const weekday = weekdayFmt.format(d);
        const label = `${weekday.charAt(0).toUpperCase() + weekday.slice(1)} (${d.getDate()}/${d.getMonth() + 1})`;
        return { date: d, label, y: revenue };
      }
      if (p.year !== undefined && p.month !== undefined && p.dayNumber !== undefined) {
        const d = buildDateFromParts(p.year, p.month, p.dayNumber) ?? new Date(p.year, (Number(p.month) > 11 ? Number(p.month) - 1 : Number(p.month)), p.dayNumber);
        const weekday = weekdayFmt.format(d);
        const label = `${weekday.charAt(0).toUpperCase() + weekday.slice(1)} (${d.getDate()}/${d.getMonth() + 1})`;
        return { date: d, label, y: revenue };
      }
      // generic fallback
      const label = String(p.day ?? p.label ?? '');
      return { date: null, label, y: revenue };
    };

    // prefer points for the current month when possible
    let filtered = (arr as any[])
      .filter(p => {
        if (p.year !== undefined && p.month !== undefined) {
          return p.month === now.getMonth() && p.year === now.getFullYear();
        }
        // keep items that already look like daily entries (dateStr)
        return !!p.dateStr;
      })
      .map(toPoint);

    if (filtered.length === 0 && (arr as any[]).length > 0) {
      // fallback: use the last 30 items and convert them to points
      const thirtyDaysAgo: Date = new Date(now);
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      thirtyDaysAgo.setHours(0, 0, 0, 0);

      filtered = (arr as any[]).filter(p => {
        const d = buildDateFromParts(p.year, p.month, p.dayNumber);
        return d !== null && d >= thirtyDaysAgo && d <= now;
      }).map(toPoint);
    }

    // keep only finite numbers (includes zeros)
    const sanitized = filtered.map(d => ({ date: d.date ?? null, label: d.label, y: Number(d.y) })).filter(d => Number.isFinite(d.y));
    return sanitized;
  }, [dashboardInfos, buildDateFromParts]);

  const barData = useMemo(() => {
    const all = dashboardInfos?.DailyPerformances ?? [];
    // sort by real date to ensure chronological order then pick last 7
    const sorted = [...all].sort((a: any, b: any) => {
      const da = a.dateStr ? new Date(a.dateStr).getTime() : (buildDateFromParts(a.year, a.month, a.dayNumber)?.getTime() ?? 0);
      const db = b.dateStr ? new Date(b.dateStr).getTime() : (buildDateFromParts(b.year, b.month, b.dayNumber)?.getTime() ?? 0);
      return da - db;
    });
    // prefer entries up to today (avoid picking future dates). If none, fallback to last available
    const now = new Date();
    const entriesWithDate = sorted.map((p: any) => ({
      src: p,
      date: p.dateStr ? new Date(p.dateStr) : (buildDateFromParts(p.year, p.month, p.dayNumber) ?? null),
    })).filter((e: any) => e.date !== null);

    // Calculate date from 7 days ago (at midnight)
    const sevenDaysAgo = new Date(now);
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    sevenDaysAgo.setHours(0, 0, 0, 0);

    const upToToday = entriesWithDate.filter((e: any) => (e.date.getTime() <= now.getTime()));
    const last7Entries = upToToday.length > 0 
      ? upToToday.filter((e: any) => e.date.getTime() >= sevenDaysAgo.getTime())
      : [];

    if (process.env.NODE_ENV !== 'production') {
      try {
        console.debug('[TabPerformances] DailyPerformances sorted (last 12):', entriesWithDate.slice(-12).map((e:any) => ({ raw: e.src, dateIso: e.date?.toISOString?.(), label: e.date ? e.date.toLocaleDateString('fr-FR') : null })));
        console.debug('[TabPerformances] last7 selected (used upToToday?:', upToToday.length>0, '):', last7Entries.map((e:any) => e));
      } catch (e) { /* ignore */ }
    }
    const weekdayFmt = new Intl.DateTimeFormat('fr-FR', { weekday: 'short' });
    const labels = last7Entries.map((e: any) => {
      const d = e.date as Date;
      const weekday = weekdayFmt.format(d);
      return `${weekday.charAt(0).toUpperCase() + weekday.slice(1)} ${d.getDate()}/${d.getMonth() + 1}`;
    });
    const values = last7Entries.map((e: any) => {
      const p = e.src;
      return (p.noonPerformance ?? 0) + (p.eveningPerformance ?? 0);
    });
    return { labels, values };
  }, [dashboardInfos, buildDateFromParts]);

  const pieData = useMemo(() => {
    const ca = monthlyRevenue ?? dashboardInfos?.todayRevenue ?? 0;
    const payroll = dashboardInfos?.Monthly?.payroll ?? dashboardInfos?.payroll ?? 0;
    return { ca, payroll };
  }, [monthlyRevenue, dashboardInfos]);

  // build previous-year series aligned with current lineData labels
  const prevYearSeries = useMemo(() => {
    if (!lineData || lineData.length === 0) return [];
    const arr = dashboardInfos?.DailyPerformances ?? [];
    return lineData.map(pt => {
      // when pt.date is available, look for same day/month but previous year
      const d = pt.date as Date | null;
      if (!d) return 0;
      const targetYear = d.getFullYear() - 1;
      const targetMonth = d.getMonth();
      const targetDay = d.getDate();
      const found = arr.find((p: any) => {
        const py = p.year;
        const pm = normalizeMonth(p.month);
        const pd = p.dayNumber ?? p.day ?? null;
        return py === targetYear && pm === targetMonth && pd === targetDay;
      });
      if (!found) return 0;
      // prefer ca fields if present, otherwise sum covers-based turnovers (same logic as toPoint)
      const f: any = found as any;
      const revenue = f.ca ?? f.revenue ?? 
        ((f.caMidi !== undefined || f.caSoir !== undefined) ? 
          (Number(f.caMidi ?? 0) + Number(f.caSoir ?? 0)) : 
          (Number(f.noonTurnover ?? 0) + Number(f.eveningTurnover ?? 0)));
      return Number(revenue || 0);
    });
  }, [lineData, dashboardInfos]);

  return (
    <div className="space-y-6">
      <ChartDashlineEffect containerRef={chartContainerRef} />
      {/* KPI Row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <KPICard title="Clients (mois courant)" value={monthClientsRounded}  />
        <KPICard title="CA (mois courant)" value={(typeof monthlyRevenue === 'number' && monthlyRevenue > 0) ? `${monthlyRevenue.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` : '-'} />
        <KPICard title="Couverts J-1 (Midi)" value={j1Noon} />
        <KPICard title="Couverts J-1 (Soir)" value={j1Evening}  />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Paper className="dp-card p-4 col-span-2" elevation={2}>
          <h3 className="text-lg font-semibold mb-2">CA - mois en cours</h3>
          <div style={{ height: 280 }}>
            {lineData.length === 0 || lineData.every(d => d.y === 0) ? (
              <div className="p-6 text-sm text-gray-400">Pas de données</div>
            ) : (
              <div ref={chartContainerRef}>
                <LineChart
                series={([
                  { data: lineData.map(d => Number(d.y)), label: 'CA actuel', id: 'caNow', color: '#c8a059'  },
                  { data: (prevYearSeries as number[]), label: `CA ${new Date().getFullYear() - 1}`, id: 'caPrev', color: '#a78e63b0' },
                ] as any)}
                xAxis={[{ data: lineData.map(d => d.label), scaleType: 'point' }]}
                height={240}
                sx={{
                  [`.${lineElementClasses.root}`]: { strokeWidth: 1 },
                }}
              />
              </div>
            )}
            {/* CA summary removed per request */}
          </div>
        </Paper>

        <Paper className="dp-card p-4" elevation={2}>
          <h3 className="text-lg font-semibold mb-2">CA vs Masse salariale</h3>
          <div className="flex items-center justify-center">
            <SmallPieChart ca={pieData.ca} payroll={pieData.payroll} size={160} />
          </div>
        </Paper>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Paper className="dp-card p-4" elevation={2}>
          <h3 className="text-lg font-semibold mb-2">Couverts  (7 derniers jours)</h3>
          <div style={{ height: 250 }}>
            <BarChart
              series={[{ data: barData.values, color : '#c8a059' }]}
              xAxis={[{ data: barData.labels, scaleType: 'band' }]}
              height={240}
            />
          </div>
        </Paper>

        {/* Effectif recommandé card removed as per request */}
      </div>
    </div>
  );
};

export default TabPerformances;